import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation, useStaggerAnimation } from '../../hooks/useScrollAnimation';

type GuideCategory = 'All Guides' | 'Beginner' | 'Intermediate' | 'Advanced' | 'Organic Methods' | 'Pest Control' | 'Harvesting';

export default function GuidesPage() {
  const [selectedCategory, setSelectedCategory] = useState<GuideCategory>('All Guides');
  const [searchQuery, setSearchQuery] = useState('');

  // Scroll animations
  const heroAnim = useScrollAnimation();
  const featuredAnim = useScrollAnimation();
  const guidesAnim = useStaggerAnimation(8);
  const sidebarAnim = useScrollAnimation();

  const categories: GuideCategory[] = [
    'All Guides',
    'Beginner',
    'Intermediate',
    'Advanced',
    'Organic Methods',
    'Pest Control',
    'Harvesting'
  ];

  const guides = [
    {
      id: 1,
      title: 'Beginner\'s Complete Seed-to-Harvest Guide',
      category: 'Beginner',
      excerpt: 'Everything you need to know to start your first cannabis grow successfully. From selecting seeds to harvesting your first crop, this comprehensive guide covers all the essentials for new cultivators.',
      readTime: '15 min read',
      difficulty: 'Beginner',
      image: 'https://public.readdy.ai/ai/img_res/d072cf96ff784c3463b4452d25ba344f.jpg',
      featured: true
    },
    {
      id: 2,
      title: 'How to Grow Cannabis Organically in Small Spaces',
      category: 'Organic Methods',
      excerpt: 'Maximize your yield in limited space using organic methods. Learn space-efficient techniques, container selection, and natural nutrient management for compact grows.',
      readTime: '10 min read',
      difficulty: 'Intermediate',
      image: 'https://readdy.ai/api/search-image?query=compact%20indoor%20cannabis%20grow%20setup%20in%20small%20apartment%20space%20with%20organic%20soil%20pots%20LED%20grow%20lights%20on%20shelves%20efficient%20space%20utilization%20clean%20modern%20aesthetic%20sustainable%20urban%20gardening%20professional%20photography&width=600&height=400&seq=guide002&orientation=landscape'
    },
    {
      id: 3,
      title: 'Organic Pest Management Without Chemicals',
      category: 'Pest Control',
      excerpt: 'Protect your plants naturally with proven organic pest control methods. Identify common pests, implement prevention strategies, and use natural remedies to keep your garden healthy.',
      readTime: '12 min read',
      difficulty: 'Intermediate',
      image: 'https://readdy.ai/api/search-image?query=healthy%20green%20cannabis%20plant%20leaves%20with%20magnifying%20glass%20examining%20for%20pests%20in%20bright%20greenhouse%20natural%20organic%20pest%20control%20methods%20ladybugs%20beneficial%20insects%20educational%20content%20clean%20professional%20photography&width=600&height=400&seq=guide003&orientation=landscape'
    },
    {
      id: 4,
      title: 'Soil, Soul, and Sunlight: The Organic Trinity',
      category: 'Organic Methods',
      excerpt: 'Discover the three pillars of successful organic cultivation. Master soil composition, understand plant energy, and optimize light exposure for thriving cannabis plants.',
      readTime: '8 min read',
      difficulty: 'Beginner',
      image: 'https://readdy.ai/api/search-image?query=rich%20dark%20organic%20soil%20in%20hands%20with%20cannabis%20plant%20in%20background%20bright%20natural%20sunlight%20outdoor%20garden%20sustainable%20farming%20connection%20to%20nature%20earthy%20tones%20professional%20photography%20holistic%20growing%20philosophy&width=600&height=400&seq=guide004&orientation=landscape'
    },
    {
      id: 5,
      title: 'Terpenes 101: Smell, Feel, Heal',
      category: 'Advanced',
      excerpt: 'Unlock the aromatic secrets of cannabis. Learn about terpene profiles, their therapeutic benefits, and how to enhance terpene production in your grow.',
      readTime: '11 min read',
      difficulty: 'Advanced',
      image: 'https://readdy.ai/api/search-image?query=close%20up%20cannabis%20trichomes%20and%20terpenes%20on%20mature%20flower%20buds%20with%20aromatic%20essential%20oil%20bottles%20scientific%20educational%20photography%20macro%20detail%20natural%20compounds%20wellness%20benefits%20clean%20aesthetic&width=600&height=400&seq=guide005&orientation=landscape'
    },
    {
      id: 6,
      title: 'Lighting Schedules Explained: Veg to Flower',
      category: 'Intermediate',
      excerpt: 'Master the art of light timing for optimal growth. Understand photoperiods, transition techniques, and how to manipulate light cycles for maximum yield.',
      readTime: '9 min read',
      difficulty: 'Intermediate',
      image: 'https://readdy.ai/api/search-image?query=indoor%20cannabis%20grow%20room%20with%20professional%20LED%20grow%20lights%20on%20timer%20system%20healthy%20plants%20in%20vegetative%20and%20flowering%20stages%20technical%20setup%20educational%20photography%20modern%20cultivation%20technology%20clean%20aesthetic&width=600&height=400&seq=guide006&orientation=landscape'
    },
    {
      id: 7,
      title: 'Watering & pH Mastery for Healthy Roots',
      category: 'Beginner',
      excerpt: 'Perfect your watering technique and pH balance. Learn proper watering schedules, pH testing methods, and how to maintain optimal root zone conditions.',
      readTime: '7 min read',
      difficulty: 'Beginner',
      image: 'https://readdy.ai/api/search-image?query=watering%20cannabis%20plants%20with%20pH%20meter%20testing%20water%20quality%20in%20organic%20soil%20healthy%20root%20system%20visible%20through%20clear%20container%20educational%20gardening%20photography%20proper%20irrigation%20techniques%20clean%20professional%20aesthetic&width=600&height=400&seq=guide007&orientation=landscape'
    },
    {
      id: 8,
      title: 'Harvesting & Curing: Timing is Everything',
      category: 'Harvesting',
      excerpt: 'Learn when and how to harvest for peak potency. Master the art of drying and curing to preserve flavor, aroma, and therapeutic properties.',
      readTime: '13 min read',
      difficulty: 'Advanced',
      image: 'https://readdy.ai/api/search-image?query=mature%20cannabis%20flowers%20ready%20for%20harvest%20with%20trichomes%20visible%20hanging%20to%20dry%20in%20controlled%20environment%20curing%20jars%20professional%20harvesting%20setup%20educational%20photography%20optimal%20timing%20techniques%20clean%20aesthetic&width=600&height=400&seq=guide008&orientation=landscape'
    },
    {
      id: 9,
      title: 'Sustainable Growing: Worm Composting Basics',
      category: 'Organic Methods',
      excerpt: 'Create nutrient-rich organic fertilizer with worm cultivation. Build your own vermicompost system and produce the perfect soil amendment for thriving plants.',
      readTime: '10 min read',
      difficulty: 'Intermediate',
      image: 'https://readdy.ai/api/search-image?query=rich%20dark%20compost%20soil%20with%20earthworms%20in%20wooden%20composting%20bin%20surrounded%20by%20green%20cannabis%20plants%20natural%20outdoor%20setting%20organic%20farming%20sustainable%20agriculture%20educational%20photography%20earthy%20natural%20tones&width=600&height=400&seq=guide009&orientation=landscape'
    },
    {
      id: 10,
      title: 'Training Techniques: LST, HST & Topping',
      category: 'Advanced',
      excerpt: 'Maximize yield through plant training methods. Learn low-stress and high-stress techniques to shape your plants for optimal light exposure and bigger harvests.',
      readTime: '14 min read',
      difficulty: 'Advanced',
      image: 'https://readdy.ai/api/search-image?query=cannabis%20plant%20training%20techniques%20low%20stress%20training%20LST%20with%20ties%20and%20stakes%20demonstrating%20topping%20and%20pruning%20methods%20educational%20diagram%20style%20professional%20photography%20plant%20manipulation%20for%20better%20yields%20clean%20aesthetic&width=600&height=400&seq=guide010&orientation=landscape'
    }
  ];

  const popularTopics = [
    'Organic Growing',
    'Indoor Setup',
    'Pest Control',
    'Nutrients',
    'Lighting',
    'Harvesting',
    'Soil Health',
    'pH Balance',
    'Training',
    'Curing'
  ];

  const filteredGuides = guides.filter(guide => {
    const matchesCategory = selectedCategory === 'All Guides' || guide.category === selectedCategory;
    const matchesSearch = guide.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         guide.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredGuide = guides.find(g => g.featured);
  const regularGuides = filteredGuides.filter(g => !g.featured);

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section
        ref={heroAnim.ref as React.RefObject<HTMLElement>}
        className="relative py-16 sm:py-20 md:py-24 px-4 overflow-hidden bg-gradient-to-br from-[#1A3A2A] to-[#2D5F3F]"
        style={{
          opacity: heroAnim.isVisible ? 1 : 0,
          transform: heroAnim.isVisible ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
        }}
      >
        <div className="absolute inset-0 opacity-10">
          <i className="ri-plant-line text-[300px] text-white absolute top-1/2 left-1/4 transform -translate-x-1/2 -translate-y-1/2 rotate-12"></i>
          <i className="ri-leaf-line text-[250px] text-white absolute top-1/3 right-1/4 transform translate-x-1/2 -rotate-12"></i>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <div className="inline-block px-4 py-2 border border-[#D4AF37]/60 rounded-full text-[#D4AF37] text-sm font-semibold tracking-widest uppercase mb-6">
            Expert Knowledge
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            Complete Growing Guides
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-[#F5F1E8] font-light max-w-3xl mx-auto">
            Master organic cannabis cultivation from seed to harvest with our comprehensive tutorials and expert techniques
          </p>
        </div>
      </section>

      {/* Category Filter Bar */}
      <section className="sticky top-20 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 sm:px-6 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-300 cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-[#2D5F3F] text-white shadow-lg scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-[#D4AF37]/20 hover:text-[#2D5F3F]'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 sm:py-16 md:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content Area */}
            <div className="lg:col-span-2">
              {/* Featured Guide */}
              {featuredGuide && selectedCategory === 'All Guides' && !searchQuery && (
                <div
                  ref={featuredAnim.ref as React.RefObject<HTMLDivElement>}
                  className="mb-12"
                  style={{
                    opacity: featuredAnim.isVisible ? 1 : 0,
                    transform: featuredAnim.isVisible ? 'translateY(0)' : 'translateY(40px)',
                    transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
                  }}
                >
                  <h2 className="text-2xl sm:text-3xl font-bold text-[#2C2C2C] mb-6 flex items-center gap-3">
                    <i className="ri-star-fill text-[#D4AF37]"></i>
                    Featured Guide
                  </h2>
                  <Link
                    to="/blog"
                    className="block group bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                  >
                    <div className="relative h-64 sm:h-80 md:h-96 overflow-hidden">
                      <img
                        src={featuredGuide.image}
                        alt={featuredGuide.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                      <div className="absolute top-4 left-4 flex gap-2">
                        <span className="px-3 py-1 bg-[#2D5F3F] text-white text-xs font-bold rounded-full">
                          {featuredGuide.category}
                        </span>
                        <span className="px-3 py-1 bg-[#D4AF37] text-white text-xs font-bold rounded-full">
                          {featuredGuide.difficulty}
                        </span>
                      </div>
                    </div>
                    <div className="p-6 sm:p-8">
                      <h3 className="text-2xl sm:text-3xl font-bold text-[#2C2C2C] mb-3 group-hover:text-[#2D5F3F] transition-colors duration-300">
                        {featuredGuide.title}
                      </h3>
                      <p className="text-base text-gray-600 leading-relaxed mb-4">
                        {featuredGuide.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500 flex items-center gap-2">
                          <i className="ri-time-line"></i>
                          {featuredGuide.readTime}
                        </span>
                        <span className="text-[#2D5F3F] font-semibold flex items-center gap-2 group-hover:gap-3 transition-all duration-300">
                          Read Guide
                          <i className="ri-arrow-right-line"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                </div>
              )}

              {/* Guides Grid */}
              <div
                ref={guidesAnim.ref as React.RefObject<HTMLDivElement>}
              >
                <h2 className="text-2xl sm:text-3xl font-bold text-[#2C2C2C] mb-6">
                  {selectedCategory === 'All Guides' ? 'All Guides' : `${selectedCategory} Guides`}
                  <span className="text-lg font-normal text-gray-500 ml-3">
                    ({regularGuides.length} {regularGuides.length === 1 ? 'guide' : 'guides'})
                  </span>
                </h2>

                {regularGuides.length === 0 ? (
                  <div className="text-center py-16">
                    <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
                    <p className="text-xl text-gray-500">No guides found matching your criteria</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {regularGuides.map((guide, index) => (
                      <Link
                        key={guide.id}
                        to="/blog"
                        className="block group bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                        style={{
                          opacity: guidesAnim.visibleItems[index] ? 1 : 0,
                          transform: guidesAnim.visibleItems[index] ? 'translateY(0)' : 'translateY(30px)',
                          transition: 'opacity 0.6s ease-out, transform 0.6s ease-out, box-shadow 0.3s, translate 0.3s'
                        }}
                      >
                        <div className="relative h-48 overflow-hidden">
                          <img
                            src={guide.image}
                            alt={guide.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-600"
                          />
                          <div className="absolute top-3 left-3 flex gap-2">
                            <span className="px-2 py-1 bg-white/90 text-[#2D5F3F] text-xs font-bold rounded-full">
                              {guide.category}
                            </span>
                          </div>
                        </div>
                        <div className="p-5">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                              guide.difficulty === 'Beginner' ? 'bg-green-100 text-green-700' :
                              guide.difficulty === 'Intermediate' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {guide.difficulty}
                            </span>
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <i className="ri-time-line"></i>
                              {guide.readTime}
                            </span>
                          </div>
                          <h3 className="text-lg font-bold text-[#2C2C2C] mb-2 line-clamp-2 group-hover:text-[#2D5F3F] transition-colors duration-300">
                            {guide.title}
                          </h3>
                          <p className="text-sm text-gray-600 leading-relaxed line-clamp-3 mb-3">
                            {guide.excerpt}
                          </p>
                          <span className="text-sm text-[#2D5F3F] font-semibold flex items-center gap-2 group-hover:gap-3 transition-all duration-300">
                            Read Guide
                            <i className="ri-arrow-right-line"></i>
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div
              ref={sidebarAnim.ref as React.RefObject<HTMLDivElement>}
              className="lg:col-span-1"
              style={{
                opacity: sidebarAnim.isVisible ? 1 : 0,
                transform: sidebarAnim.isVisible ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
              }}
            >
              {/* Search Bar */}
              <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                <h3 className="text-lg font-bold text-[#2C2C2C] mb-4 flex items-center gap-2">
                  <i className="ri-search-line text-[#2D5F3F]"></i>
                  Search Guides
                </h3>
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search topics..."
                    className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D5F3F] transition-shadow duration-300 text-sm"
                  />
                  <i className="ri-search-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                </div>
              </div>

              {/* Popular Topics */}
              <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                <h3 className="text-lg font-bold text-[#2C2C2C] mb-4 flex items-center gap-2">
                  <i className="ri-fire-line text-[#D4AF37]"></i>
                  Popular Topics
                </h3>
                <div className="flex flex-wrap gap-2">
                  {popularTopics.map((topic) => (
                    <button
                      key={topic}
                      onClick={() => setSearchQuery(topic)}
                      className="px-3 py-1.5 bg-[#2D5F3F]/10 text-[#2D5F3F] text-sm font-semibold rounded-full hover:bg-[#2D5F3F] hover:text-white transition-all duration-300 cursor-pointer"
                    >
                      {topic}
                    </button>
                  ))}
                </div>
              </div>

              {/* Newsletter Widget */}
              <div className="bg-gradient-to-br from-[#1A3A2A] to-[#2D5F3F] rounded-xl shadow-lg p-6 mb-6 text-white">
                <i className="ri-mail-line text-4xl text-[#D4AF37] mb-3"></i>
                <h3 className="text-xl font-bold mb-2">Get Growing Tips</h3>
                <p className="text-sm text-white/90 mb-4">
                  Weekly expert advice delivered to your inbox
                </p>
                <input
                  type="email"
                  placeholder="Your email"
                  className="w-full px-4 py-2 rounded-lg text-gray-800 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                />
                <button className="w-full bg-[#D4AF37] text-[#1A3A2A] py-2 rounded-lg font-semibold hover:bg-[#F5F1E8] transition-all duration-300 cursor-pointer whitespace-nowrap text-sm">
                  Subscribe
                </button>
              </div>

              {/* Affiliate Banners - Seeds */}
              <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                <h3 className="text-lg font-bold text-[#2C2C2C] mb-4 flex items-center gap-2">
                  <i className="ri-seedling-line text-[#2D5F3F]"></i>
                  Premium Seeds
                </h3>
                <div className="space-y-4">
                  <a
                    href="https://www.greenaffiliates.com/accounts/default1/nxkkkeyb/25b3944c.html?a_aid=ElixirSue"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block hover:opacity-80 transition-opacity duration-300 cursor-pointer"
                  >
                    <img
                      src="https://www.greenaffiliates.com/accounts/default1/nxkkkeyb/25b3944c.png"
                      alt="Crop King Seeds"
                      title="Crop King Seeds - Premium Cannabis Seeds"
                      width={250}
                      height={250}
                      className="w-full rounded-lg shadow-md"
                    />
                  </a>
                  <img
                    alt=""
                    src="https://www.greenaffiliates.com/scripts/nxkkkeyi?a_aid=ElixirSue&a_bid=25b3944c"
                    width={1}
                    height={1}
                  />

                  <a
                    href="https://www.greenaffiliates.com/accounts/default1/nxkkkeyb/13d1fb1f.html?a_aid=ElixirSue"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block hover:opacity-80 transition-opacity duration-300 cursor-pointer"
                  >
                    <img
                      src="https://www.greenaffiliates.com/accounts/default1/nxkkkeyb/13d1fb1f.png"
                      alt="Rocket Seeds"
                      title="Rocket Seeds - Quality Cannabis Seeds"
                      width={250}
                      height={250}
                      className="w-full rounded-lg shadow-md"
                    />
                  </a>
                  <img
                    alt=""
                    src="https://www.greenaffiliates.com/scripts/nxkkkeyi?a_aid=ElixirSue&a_bid=13d1fb1f"
                    width={1}
                    height={1}
                  />
                </div>
              </div>

              {/* Help Section */}
              <div className="bg-[#F8FAF9] rounded-xl p-6">
                <i className="ri-question-line text-3xl text-[#2D5F3F] mb-3"></i>
                <h3 className="text-lg font-bold text-[#2C2C2C] mb-2">Need Help?</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Can't find what you're looking for? Our community is here to help.
                </p>
                <a
                  href="#community"
                  className="inline-flex items-center gap-2 text-sm text-[#2D5F3F] font-semibold hover:gap-3 transition-all duration-300 cursor-pointer"
                >
                  Join Community
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}